import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { X, Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMAIL_KEY = "vi_cat_email";
const NEWS_KEY = "vi_cat_news";
const DISMISS_KEY = "vi_cat_dismissed";

const fmtLeft = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${d}d ${h}h ${String(m).padStart(2, "0")}m ${String(sec).padStart(2, "0")}s`;
};

export const CreateathonBanner = () => {
  const { user } = useAuth();
  const [active, setActive] = useState(false);
  const [endsAt, setEndsAt] = useState(null);
  const [msLeft, setMsLeft] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === "1");
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(() => localStorage.getItem(EMAIL_KEY) || "");
  const [news, setNews] = useState(() => localStorage.getItem(NEWS_KEY) || "");
  const [busy, setBusy] = useState(false);
  const [eligible, setEligible] = useState(true);

  useEffect(() => {
    api.get("/createathon").then((r) => {
      setActive(!!r.data.active);
      setEndsAt(r.data.ends_at || null);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    // logged-in users who already redeemed/subscribed shouldn't see a $15 promise checkout won't honor
    if (user && joined) {
      api.get("/checkout/options").then((r) => setEligible(!!r.data.createathon?.eligible)).catch(() => {});
    }
  }, [user, joined]);

  useEffect(() => {
    if (!active || !endsAt) return;
    const tick = () => {
      const left = new Date(endsAt).getTime() - Date.now();
      setMsLeft(left);
      if (left <= 0) setActive(false);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [active, endsAt]);

  if (!active || dismissed || !eligible) return null;

  const join = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/createathon/join", { email });
      const em = email.trim().toLowerCase();
      localStorage.setItem(EMAIL_KEY, em);
      setJoined(em);
      toast.success(data.message);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const answerNews = async (yes) => {
    localStorage.setItem(NEWS_KEY, yes ? "y" : "n");
    setNews(yes ? "y" : "n");
    if (yes) {
      try {
        await api.post("/newsletter/subscribe", { email: joined });
        toast.success("You're on the list — tips & tricks incoming!");
      } catch (err) {
        toast.error(apiError(err));
      }
    }
  };

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const claimBtn = (
    <Link
      to={user ? "/credits" : "/register"}
      data-testid="createathon-claim-btn"
      className="shrink-0 bg-fuchsia-600 hover:bg-fuchsia-500 px-4 py-2 rounded-md text-sm font-bold text-white transition-colors shadow-[0_0_16px_rgba(255,46,196,0.45)]"
    >
      Claim first month for $15 →
    </Link>
  );

  return (
    <div className="fixed bottom-0 inset-x-0 z-40" data-testid="createathon-banner">
      <div className="bg-[#0c0114]/95 backdrop-blur-xl border-t neon-cat-border px-4 py-3">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2.5 sm:gap-5 pr-8">
          <p className="neon-cat-text font-head font-black text-sm sm:text-base whitespace-nowrap">
            🎉 VIDEO CREATE-A-THON <span className="text-fuchsia-300">— 20% OFF</span>
          </p>
          {msLeft !== null && msLeft > 0 && (
            <span
              data-testid="createathon-countdown"
              className="whitespace-nowrap text-xs font-head font-bold text-amber-300 border border-amber-400/40 bg-amber-500/10 rounded-full px-3 py-1 tabular-nums shadow-[0_0_12px_rgba(251,191,36,0.35)]"
            >
              ⏳ Ends in {fmtLeft(msLeft)}
            </span>
          )}

          {!joined && (
            <form onSubmit={join} className="flex items-center gap-2 flex-wrap justify-center" data-testid="createathon-join-form">
              <span className="text-sm text-zinc-300 whitespace-nowrap">
                First month of Starter: <s className="text-zinc-500">$19</s> <span className="neon-cat-price font-head font-bold">$15</span>
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="createathon-email-input"
                className="w-44 sm:w-52 bg-black/60 border border-fuchsia-500/40 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="createathon-join-btn"
                className="bg-fuchsia-600 hover:bg-fuchsia-500 px-4 py-2 rounded-md text-sm font-bold text-white transition-colors disabled:opacity-60 shadow-[0_0_16px_rgba(255,46,196,0.45)]"
              >
                {busy ? "Locking in…" : "Get my deal"}
              </button>
            </form>
          )}

          {joined && !news && (
            <div className="flex items-center gap-3 flex-wrap justify-center" data-testid="createathon-offer-step">
              <span className="text-sm text-zinc-200 whitespace-nowrap">
                Locked in! First month <s className="text-zinc-500">$19</s> <span className="neon-cat-price font-head font-bold">$15</span>
              </span>
              {claimBtn}
              <span className="text-xs text-zinc-400 whitespace-nowrap">FREE newsletter with tips &amp; tricks?</span>
              <div className="flex gap-1.5">
                <button onClick={() => answerNews(true)} data-testid="createathon-news-yes" className="px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/35 transition-colors">
                  Yes please
                </button>
                <button onClick={() => answerNews(false)} data-testid="createathon-news-no" className="px-3 py-1.5 rounded-md text-xs font-semibold bg-white/5 border border-white/10 text-zinc-400 hover:bg-white/10 transition-colors">
                  No thanks
                </button>
              </div>
            </div>
          )}

          {joined && news && (
            <div className="flex items-center gap-3 flex-wrap justify-center" data-testid="createathon-locked">
              <span className="text-sm text-zinc-200 whitespace-nowrap flex items-center gap-1.5">
                <Check className="h-4 w-4 text-emerald-400" /> Your <span className="neon-cat-price font-head font-bold">$15</span> first month is locked in
                {news === "y" && <span className="text-xs text-emerald-400">· newsletter on ✓</span>}
              </span>
              {claimBtn}
            </div>
          )}
        </div>
        <button onClick={dismiss} data-testid="createathon-dismiss" aria-label="Dismiss" className="absolute top-2.5 right-3 text-zinc-500 hover:text-white transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
